import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { AdminProvider } from './contexts/AdminContext';
import { TeacherProvider } from './contexts/TeacherContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import AdminRoute from './components/admin/AdminRoute';
import TeacherRoute from './components/teacher/TeacherRoute';
import NavigationBar from './components/NavigationBar';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ChartPage from './pages/ChartPage';
import QuestionsPage from './pages/QuestionsPage';
import QuestionDetailPage from './pages/QuestionDetailPage';
import AnswerPage from './pages/AnswerPage';
import QueryHistoryPage from './pages/QueryHistoryPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import PairAnalysisPage from './pages/PairAnalysisPage';
import PairResultPage from './pages/PairResultPage';
import TestGoogleAuthPage from './pages/TestGoogleAuthPage';
// 管理員頁面
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import TransactionManagementPage from './pages/admin/TransactionManagementPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';
import CurrencyManagementPage from './pages/admin/CurrencyManagementPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminQuestionsPage from './pages/admin/AdminQuestionsPage';
import AdminPricePage from './pages/admin/AdminPricePage';
import AdminAnnouncementsPage from './pages/admin/AdminAnnouncementsPage';
import AdminBackupPage from './pages/admin/AdminBackupPage';
// 大師模式頁面
import TeacherLoginPage from './pages/teacher/TeacherLoginPage';
import TeacherDashboardPage from './pages/teacher/TeacherDashboardPage';
import TeacherConsulteePage from './pages/teacher/TeacherConsulteePage';
import InvitePage from './pages/InvitePage';
import ShareViewPage from './pages/ShareViewPage';
import LandingPage from './pages/LandingPage';
import ColdStartOverlay from './components/ColdStartOverlay';

/**
 * Material-UI 主題配置
 */
const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
    },
    secondary: {
      main: '#764ba2',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Noto Sans TC"',
      '"Microsoft JhengHei"',
    ].join(','),
  },
});

/**
 * Layout 組件 - 處理導航欄顯示
 */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // 不顯示導航欄的頁面（包含管理員頁面）
  const hideNavbarPaths = ['/', '/login', '/register', '/test-google-auth'];
  const isAdminPath = location.pathname.startsWith('/admin');
  const isTeacherPath = location.pathname.startsWith('/teacher');
  const isInvitePath = location.pathname.startsWith('/invite');
  const isSharePath = location.pathname.startsWith('/share');
  const shouldShowNavbar =
    !hideNavbarPaths.includes(location.pathname) && !isAdminPath && !isTeacherPath && !isInvitePath && !isSharePath;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {shouldShowNavbar && <NavigationBar />}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
    </Box>
  );
};

/**
 * 主應用程式組件
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ColdStartOverlay />
      <AuthProvider>
        <CurrencyProvider>
          <AdminProvider>
          <TeacherProvider>
            <Router>
              <Layout>
                <Routes>
                {/* 公開路由 */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/test-google-auth" element={<TestGoogleAuthPage />} />
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/share/:token" element={<ShareViewPage />} />

                {/* 管理員路由 */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <UserManagementPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId"
                  element={
                    <AdminRoute>
                      <UserDetailPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/transactions"
                  element={
                    <AdminRoute>
                      <TransactionManagementPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/logs"
                  element={
                    <AdminRoute>
                      <AdminLogsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/currencies"
                  element={
                    <AdminRoute>
                      <CurrencyManagementPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <AdminRoute requiredRoles={['super_admin']}>
                      <AdminSettingsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/questions"
                  element={
                    <AdminRoute>
                      <AdminQuestionsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/price"
                  element={
                    <AdminRoute>
                      <AdminPricePage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/announcements"
                  element={
                    <AdminRoute>
                      <AdminAnnouncementsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/backup"
                  element={
                    <AdminRoute requiredRoles={['super_admin', 'admin']}>
                      <AdminBackupPage />
                    </AdminRoute>
                  }
                />
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

                {/* 大師模式路由 */}
                <Route path="/teacher/login" element={<TeacherLoginPage />} />
                <Route
                  path="/teacher/dashboard"
                  element={
                    <TeacherRoute>
                      <TeacherDashboardPage />
                    </TeacherRoute>
                  }
                />
                <Route
                  path="/teacher/consultees/:id"
                  element={
                    <TeacherRoute>
                      <TeacherConsulteePage />
                    </TeacherRoute>
                  }
                />
                <Route path="/teacher" element={<Navigate to="/teacher/dashboard" replace />} />

                {/* 受保護路由 */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/chart"
                  element={
                    <ProtectedRoute>
                      <ChartPage />
                    </ProtectedRoute>
                }
              />

              <Route
                path="/questions"
                element={
                  <ProtectedRoute>
                    <QuestionsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/questions/:question_id"
                element={
                  <ProtectedRoute>
                    <QuestionDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/answer"
                element={
                  <ProtectedRoute>
                    <AnswerPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/answer/:query_id"
                element={
                  <ProtectedRoute>
                    <AnswerPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <QueryHistoryPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <TransactionHistoryPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pair"
                element={
                  <ProtectedRoute>
                    <PairAnalysisPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pair-analysis"
                element={
                  <ProtectedRoute>
                    <PairAnalysisPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/pair/result"
                element={
                  <ProtectedRoute>
                    <PairResultPage />
                  </ProtectedRoute>
                }
              />

              {/* 預設路由 - 公開首頁 */}
              <Route path="/" element={<LandingPage />} />

              {/* 404 路由 */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </Router>
              </TeacherProvider>
          </AdminProvider>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
